// ===================================================================
// MainStreet — Re-entry Checkpoint Scanner
// Persistent camera QR scanner for checkpoint staff
// Scans passes, shows instant result, auto-resets for next scan
// ===================================================================

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Stack,
  Tabs, Table, Center, Loader,
} from '../../components/gab/GabComponents';
import {
  IconScan, IconSearch, IconList, IconCheck, IconX, IconAlertTriangle,
  IconRefresh,
} from '@tabler/icons-react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { useMainStreetOpsStore, type MSReentryPass } from '../../store/mainstreetOpsStore';
import { fmtDate, fmtDateTime } from '../../lib/dateFormat';

interface ScanResult {
  code: string;
  pass: MSReentryPass | null;
  status: 'valid' | 'expired' | 'revoked' | 'not_found';
  timestamp: string;
  authTitle: string;
}

const STATUS_CONFIG = {
  valid: { color: '#16a34a', bg: '#dcfce7', label: 'VALID', icon: IconCheck },
  expired: { color: '#d97706', bg: '#fef3c7', label: 'EXPIRED', icon: IconAlertTriangle },
  revoked: { color: '#dc2626', bg: '#fee2e2', label: 'REVOKED', icon: IconX },
  not_found: { color: '#6b7280', bg: '#f3f4f6', label: 'NOT FOUND', icon: IconX },
};

export default function Checkpoint() {
  const {
    reentryPasses, reentryAuths, loading,
    loadReentryPasses, loadReentryAuths, verifyPass,
  } = useMainStreetOpsStore();

  const LOG_PAGE_SIZE = 50;
  const [logPage, setLogPage] = useState(1);
  const PASS_PAGE_SIZE = 25;
  const [passPage, setPassPage] = useState(1);

  const [activeTab, setActiveTab] = useState<string | null>('scanner');
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [scanLog, setScanLog] = useState<ScanResult[]>([]);
  const [manualCode, setManualCode] = useState('');
  const [passSearch, setPassSearch] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserQRCodeReader | null>(null);
  const controlsRef = useRef<any>(null);
  const lastScannedRef = useRef('');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { loadReentryPasses(); loadReentryAuths(); }, []);

  const getAuthTitle = useCallback((authId: string) => {
    const auth = reentryAuths.find(a => String(a.Rid) === authId || String(a.id) === authId);
    return auth?.title ?? '';
  }, [reentryAuths]);

  // Process a scanned/entered code
  const processCode = useCallback(async (rawCode: string) => {
    // Extract RE- code from URL or raw input
    let code = rawCode.trim();
    const reMatch = code.match(/RE-[A-Z0-9]{6}/i);
    if (reMatch) code = reMatch[0].toUpperCase();

    // Prevent rapid re-scan of same code
    if (code === lastScannedRef.current) return;
    lastScannedRef.current = code;

    // Find pass
    const pass = reentryPasses.find(p => p.code.toUpperCase() === code.toUpperCase()) || null;

    let status: ScanResult['status'] = 'not_found';
    if (pass) {
      const isExpired = pass.expiresAt && new Date(pass.expiresAt) < new Date();
      if (pass.status === 'revoked') status = 'revoked';
      else if (isExpired) status = 'expired';
      else status = 'valid';

      // Mark as verified if valid
      if (status === 'valid' && !pass.verifiedAt) {
        try { await verifyPass(code); } catch { /* non-blocking */ }
      }
    }

    const result: ScanResult = {
      code,
      pass,
      status,
      timestamp: new Date().toISOString(),
      authTitle: pass ? getAuthTitle(pass.authId) : '',
    };

    setLastResult(result);
    setScanLog(prev => [result, ...prev].slice(0, 50));

    // Play sound feedback
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.3;
      if (status === 'valid') { osc.frequency.value = 880; osc.type = 'sine'; }
      else if (status === 'expired') { osc.frequency.value = 440; osc.type = 'triangle'; }
      else { osc.frequency.value = 220; osc.type = 'square'; }
      osc.start();
      osc.stop(ctx.currentTime + (status === 'valid' ? 0.15 : 0.3));
    } catch { /* audio not supported */ }

    // Auto-reset after 4 seconds
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setLastResult(null);
      lastScannedRef.current = '';
    }, 4000);
  }, [reentryPasses, getAuthTitle, verifyPass]);

  // Keep a ref to the latest processCode so the camera callback never goes stale
  const processCodeRef = useRef(processCode);
  processCodeRef.current = processCode;

  // Start camera scanning
  const startScanning = useCallback(async () => {
    if (!videoRef.current) return;
    setScanning(true);
    try {
      const reader = new BrowserQRCodeReader();
      readerRef.current = reader;
      controlsRef.current = await reader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result) => {
          if (result) processCodeRef.current(result.getText());
        }
      );
    } catch (err) {
      console.error('Camera error:', err);
      setScanning(false);
    }
  }, [processCode]);

  const stopScanning = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }
    setScanning(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controlsRef.current) controlsRef.current.stop();
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  // Auto-start scanning when scanner tab is active
  useEffect(() => {
    if (activeTab === 'scanner') {
      // Small delay for video element to mount
      const t = setTimeout(startScanning, 300);
      return () => { clearTimeout(t); stopScanning(); };
    } else {
      stopScanning();
    }
  }, [activeTab]);

  const handleManualLookup = () => {
    if (manualCode.trim()) {
      processCode(manualCode.trim());
      setManualCode('');
    }
  };

  // Pass list for lookup tab
  const filteredPasses = useMemo(() => {
    let list = [...reentryPasses].sort((a, b) => (b.issuedAt || '').localeCompare(a.issuedAt || ''));
    if (passSearch) {
      const q = passSearch.toLowerCase();
      list = list.filter(p =>
        (p.orgName || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [reentryPasses, passSearch]);

  useEffect(() => { setLogPage(1); }, [scanLog.length]);
  const logTotalPages = Math.max(1, Math.ceil(scanLog.length / LOG_PAGE_SIZE));
  const logPageRows = scanLog.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE);

  useEffect(() => { setPassPage(1); }, [filteredPasses.length]);
  const passTotalPages = Math.max(1, Math.ceil(filteredPasses.length / PASS_PAGE_SIZE));
  const passPageRows = filteredPasses.slice((passPage - 1) * PASS_PAGE_SIZE, passPage * PASS_PAGE_SIZE);

  const cfg = lastResult ? STATUS_CONFIG[lastResult.status] : null;
  const ResultIcon = cfg?.icon || IconCheck;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Checkpoint Scanner</Title>
          <Text size="xs" c="dimmed">Scan re-entry passes to verify authorization</Text>
        </div>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          onClick={() => { loadReentryPasses(); loadReentryAuths(); }}>
          Refresh
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="scanner" leftSection={<IconScan size={14} />}>Scanner</Tabs.Tab>
          <Tabs.Tab value="log" leftSection={<IconList size={14} />}>
            Log ({scanLog.length})
          </Tabs.Tab>
          <Tabs.Tab value="passes" leftSection={<IconSearch size={14} />}>
            All Passes ({reentryPasses.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* Scanner Tab */}
        <Tabs.Panel value="scanner">
          <Stack gap="md">
            {/* Camera viewfinder */}
            <Paper withBorder radius="md" style={{ overflow: 'hidden', position: 'relative', background: '#000' }}>
              <video
                ref={videoRef}
                style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
                muted
                playsInline
              />
              {!scanning && (
                <Center style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)' }}>
                  <Button color="blue" leftSection={<IconScan size={16} />} onClick={startScanning}>
                    Start Camera
                  </Button>
                </Center>
              )}
              {/* Scan frame overlay */}
              {scanning && !lastResult && (
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: 180, height: 180, border: '3px solid rgba(255,255,255,0.5)', borderRadius: 12,
                }} />
              )}
            </Paper>

            {/* Result overlay */}
            {lastResult && cfg && (
              <Paper p="lg" radius="md" style={{ background: cfg.bg, border: `2px solid ${cfg.color}`, textAlign: 'center' }}>
                <ResultIcon size={48} color={cfg.color} style={{ marginBottom: 8 }} />
                <Text size="xl" fw={800} c={cfg.color} mb={4}>{cfg.label}</Text>
                <Text size="sm" fw={600} mb={2}>{lastResult.code}</Text>
                {lastResult.pass && (
                  <>
                    <Text size="md" fw={600}>{lastResult.pass.orgName}</Text>
                    {lastResult.authTitle && <Text size="xs" c="dimmed">{lastResult.authTitle}</Text>}
                    <Text size="xs" c="dimmed" mt={4}>
                      Issued: {fmtDate(lastResult.pass.issuedAt)} — Expires: {fmtDate(lastResult.pass.expiresAt)}
                    </Text>
                  </>
                )}
                {!lastResult.pass && (
                  <Text size="sm" c="dimmed">No matching pass found for this code.</Text>
                )}
              </Paper>
            )}

            {/* Manual entry */}
            <Paper p="sm" withBorder>
              <Group gap="xs">
                <TextInput
                  placeholder="Enter code manually (RE-XXXXXX)"
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleManualLookup()}
                  size="sm"
                  style={{ flex: 1 }}
                  styles={{ input: { fontFamily: 'monospace', letterSpacing: 1 } }}
                />
                <Button size="sm" onClick={handleManualLookup} disabled={!manualCode.trim()}>
                  Lookup
                </Button>
              </Group>
            </Paper>

            <Text size="xs" c="dimmed" ta="center">
              Point camera at QR code on re-entry pass. Result appears automatically.
            </Text>
          </Stack>
        </Tabs.Panel>

        {/* Scan Log Tab */}
        <Tabs.Panel value="log">
          {scanLog.length === 0 ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">No scans yet this session.</Text>
            </Paper>
          ) : (
            <>
              <Paper withBorder>
                <Table striped fz="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Time</Table.Th>
                      <Table.Th>Code</Table.Th>
                      <Table.Th>Organization</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {logPageRows.map((s, i) => {
                      const c = STATUS_CONFIG[s.status];
                      return (
                        <Table.Tr key={i}>
                          <Table.Td><Text size="xs">{fmtDateTime(s.timestamp)}</Text></Table.Td>
                          <Table.Td><Text size="xs" ff="monospace">{s.code}</Text></Table.Td>
                          <Table.Td><Text size="xs">{s.pass?.orgName || '—'}</Text></Table.Td>
                          <Table.Td>
                            <Badge size="xs" color={c.color} variant="filled">{c.label}</Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
              {logTotalPages > 1 && (
                <Group justify="center" mt="sm" gap="xs">
                  <Button variant="subtle" size="xs" disabled={logPage === 1} onClick={() => setLogPage(1)}>««</Button>
                  <Button variant="subtle" size="xs" disabled={logPage === 1} onClick={() => setLogPage(p => p - 1)}>‹ Prev</Button>
                  {Array.from({ length: Math.min(logTotalPages, 7) }, (_, i) => {
                    const p = logTotalPages <= 7 ? i + 1 : logPage <= 4 ? i + 1 : logPage >= logTotalPages - 3 ? logTotalPages - 6 + i : logPage - 3 + i;
                    return <Button key={p} variant={p === logPage ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setLogPage(p)}>{p}</Button>;
                  })}
                  <Button variant="subtle" size="xs" disabled={logPage === logTotalPages} onClick={() => setLogPage(p => p + 1)}>Next ›</Button>
                  <Button variant="subtle" size="xs" disabled={logPage === logTotalPages} onClick={() => setLogPage(logTotalPages)}>»»</Button>
                </Group>
              )}
            </>
          )}
        </Tabs.Panel>

        {/* Pass List Tab */}
        <Tabs.Panel value="passes">
          <Stack gap="sm">
            <TextInput
              placeholder="Search by org or code..."
              leftSection={<IconSearch size={14} />}
              value={passSearch}
              onChange={e => setPassSearch(e.target.value)}
              size="sm"
            />
            {loading ? (
              <Center p="xl"><Loader /></Center>
            ) : (
              <>
                <Paper withBorder>
                  <Table striped highlightOnHover fz="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Organization</Table.Th>
                        <Table.Th>Code</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Expires</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {passPageRows.map(p => {
                        const isExpired = p.expiresAt && new Date(p.expiresAt) < new Date();
                        const displayStatus = isExpired && p.status === 'issued' ? 'expired' : p.status;
                        return (
                          <Table.Tr key={p.id} style={{ cursor: 'pointer' }}
                            onClick={() => processCode(p.code)}>
                            <Table.Td fw={500}>{p.orgName || '—'}</Table.Td>
                            <Table.Td><Text ff="monospace" size="xs">{p.code}</Text></Table.Td>
                            <Table.Td>
                              <Badge size="xs" color={
                                displayStatus === 'revoked' ? 'red' :
                                displayStatus === 'expired' ? 'orange' :
                                displayStatus === 'verified' ? 'teal' : 'blue'
                              }>{displayStatus}</Badge>
                            </Table.Td>
                            <Table.Td><Text size="xs" c={isExpired ? 'red' : 'dimmed'}>{fmtDate(p.expiresAt)}</Text></Table.Td>
                          </Table.Tr>
                        );
                      })}
                      {filteredPasses.length === 0 && (
                        <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed" ta="center" py="lg">No passes found.</Text></Table.Td></Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
                {passTotalPages > 1 && (
                  <Group justify="center" mt="sm" gap="xs">
                    <Button variant="subtle" size="xs" disabled={passPage === 1} onClick={() => setPassPage(1)}>««</Button>
                    <Button variant="subtle" size="xs" disabled={passPage === 1} onClick={() => setPassPage(p => p - 1)}>‹ Prev</Button>
                    {Array.from({ length: Math.min(passTotalPages, 7) }, (_, i) => {
                      const p = passTotalPages <= 7 ? i + 1 : passPage <= 4 ? i + 1 : passPage >= passTotalPages - 3 ? passTotalPages - 6 + i : passPage - 3 + i;
                      return <Button key={p} variant={p === passPage ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPassPage(p)}>{p}</Button>;
                    })}
                    <Button variant="subtle" size="xs" disabled={passPage === passTotalPages} onClick={() => setPassPage(p => p + 1)}>Next ›</Button>
                    <Button variant="subtle" size="xs" disabled={passPage === passTotalPages} onClick={() => setPassPage(passTotalPages)}>»»</Button>
                  </Group>
                )}
              </>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
